import { useEffect } from 'react';
import { validatePageSEO } from '../utils/seo-validators';

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  image?: string;
  structuredData?: any;
  schemas?: any[];
}

/**
 * Hook for validating SEO metadata
 * Logs warnings and errors to console when enabled
 * Controlled by window.ENV.SEO_DEBUGGER_ENABLED (set in public/config.js)
 *
 * Usage:
 * ```tsx
 * useSEOValidation({
 *   title,
 *   description,
 *   canonical,
 *   image,
 *   structuredData,
 *   schemas
 * });
 * ```
 */
/* eslint-disable no-console */
export const useSEOValidation = (props: SEOProps): void => {
  const { title, description, canonical, image, structuredData, schemas } = props;

  useEffect(() => {
    // Only run validation if SEO_DEBUGGER_ENABLED is set to 'true'
    if (!(window as any).ENV?.SEO_DEBUGGER_ENABLED || (window as any).ENV.SEO_DEBUGGER_ENABLED !== 'true') {
      return;
    }

    const result = validatePageSEO({
      title,
      description,
      canonical,
      image,
      structuredData,
      schemas,
    });

    // Use console.log for all output to avoid conflicts with console override systems
    // that may be wrapping console.error and console.warn
    if (result.errors.length > 0) {
      console.log('%c🚨 SEO Validation Errors', 'color: #ff0000; font-weight: bold');
      result.errors.forEach(error => {
        console.log(`%c  ❌ ${error}`, 'color: #ff0000');
      });
    }

    if (result.warnings.length > 0) {
      console.log('%c⚠️  SEO Validation Warnings', 'color: #ff9800; font-weight: bold');
      result.warnings.forEach(warning => {
        console.log(`%c  ⚠️  ${warning}`, 'color: #ff9800');
      });
    }

    if (result.errors.length === 0 && result.warnings.length === 0) {
      console.log('%c✅ SEO validation passed for: ' + title, 'color: #4caf50');
    }
    // Only re-run when individual values change, not when props object reference changes
  }, [title, description, canonical, image, JSON.stringify(structuredData), JSON.stringify(schemas)]);
};

export default useSEOValidation;
